import React from "react"
import { Link, useParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAnglesLeft } from "@fortawesome/free-solid-svg-icons"

import { DOCS_SECTIONS, pageRoute } from "features/Docs/manifest"
import { DocsPage } from "features/Docs/types/docs.types"

interface DocsNavProps {
  /** Called after a page link is chosen (e.g. to close the mobile drawer). */
  onNavigate?: () => void
  /**
   * Desktop only: when provided, a collapse control is rendered in the nav
   * header row that hides the navigation tree. Omitted for the mobile drawer,
   * which has its own close affordance.
   */
  onCollapse?: () => void
  /** Ref to the collapse button, so the container can manage focus on toggle. */
  collapseButtonRef?: React.Ref<HTMLButtonElement>
}

/** A contiguous run of pages sharing the same (optional) group label. */
interface PageGroup {
  group?: string
  pages: DocsPage[]
}

/**
 * Chunk a section's pages into contiguous runs by group label, preserving the
 * manifest order. Pages without a group form their own ungrouped run. Because
 * groups are contiguous by contract, this never splits a group in two.
 */
const groupPages = (pages: DocsPage[]): PageGroup[] => {
  const runs: PageGroup[] = []
  pages.forEach((page) => {
    const last = runs[runs.length - 1]
    if (last && last.group === page.group) {
      last.pages.push(page)
    } else {
      runs.push({ group: page.group, pages: [page] })
    }
  })
  return runs
}

/** The full section → page navigation tree, with the current page marked. */
const DocsNav: React.FC<DocsNavProps> = ({
  onNavigate,
  onCollapse,
  collapseButtonRef,
}) => {
  const { section: sectionSlug, page: pageSlug } = useParams<{
    section?: string
    page?: string
  }>()

  return (
    <nav className="docs-nav" aria-label="Documentation sections">
      {onCollapse && (
        <div className="docs-nav__header">
          <button
            type="button"
            ref={collapseButtonRef}
            className="dq-btn dq-btn--icon dq-btn--quiet"
            onClick={onCollapse}
            aria-label="Hide documentation navigation"
          >
            <FontAwesomeIcon icon={faAnglesLeft} />
          </button>
        </div>
      )}
      {DOCS_SECTIONS.map((section) => (
        <div className="docs-nav__section" key={section.slug}>
          <p className="docs-nav__section-title">{section.title}</p>
          {groupPages(section.pages).map((run, runIndex) => (
            <div
              className={`docs-nav__group${
                run.group ? " docs-nav__group--labelled" : ""
              }`}
              key={run.group ?? `ungrouped-${runIndex}`}
            >
              {run.group && (
                <p className="docs-nav__group-title">{run.group}</p>
              )}
              <ul className="docs-nav__list">
                {run.pages.map((page) => {
                  const active =
                    section.slug === sectionSlug && page.slug === pageSlug
                  return (
                    <li key={page.slug}>
                      <Link
                        to={pageRoute(section.slug, page.slug)}
                        className={`docs-nav__link${
                          active ? " docs-nav__link--active" : ""
                        }`}
                        aria-current={active ? "page" : undefined}
                        onClick={onNavigate}
                      >
                        {page.title}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </nav>
  )
}

export default DocsNav
